import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Logo } from '../components/Logo';
import { Lock, Mail, ArrowRight, Users, Briefcase, MapPin, Droplets } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      // Iter60 — mensaje legible en español, ocultando códigos HTTP del backend
      const raw = (err?.message || '').toLowerCase();
      const esCredenciales =
        raw.includes('401') ||
        raw.includes('credencial') ||
        raw.includes('incorrect') ||
        raw.includes('invalid') ||
        raw.includes('no autoriz');
      if (esCredenciales) {
        setError('Credenciales incorrectas. Verifica usuario y contraseña.');
      } else if (raw.includes('failed to fetch') || raw.includes('network')) {
        setError('No se puede conectar con el servidor. Inténtalo de nuevo en unos segundos.');
      } else {
        setError('No se pudo iniciar sesión. Si el problema persiste, contacta con el administrador.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-white">
      {/* Panel izquierdo – oscuro elegante */}
      <div className="hidden lg:flex relative overflow-hidden bg-brand-panel">
        <div className="absolute inset-0 bg-grid-subtle opacity-40" />

        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-20 w-full">
          <Logo size="xl" inverted />

          <div className="max-w-xl animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-ink-300 text-xs tracking-wider uppercase mb-8">
              <Droplets className="w-3.5 h-3.5 text-brand-500" />
              Sector agua potable y saneamiento
            </div>
            <h1 className="text-4xl xl:text-5xl font-heading font-bold text-white leading-[1.1] mb-6">
              El CRM pensado para el <br />
              <span className="text-brand-500">modelo B2B2G</span>
            </h1>
            <p className="text-ink-300 text-lg leading-relaxed">
              Ayuntamientos, concesionarias, distribuidores y oficinas técnicas
              — centralizados. Prescribe, licita y vende con trazabilidad completa.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <FeaturePill icon={<Users className="w-4 h-4" />} label="Clientes B2B2G" />
            <FeaturePill icon={<Briefcase className="w-4 h-4" />} label="Licitaciones" />
            <FeaturePill icon={<MapPin className="w-4 h-4" />} label="Mapa España" />
          </div>
        </div>
      </div>

      {/* Panel derecho – formulario limpio */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex justify-center">
            <Logo size="lg" />
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-heading font-bold text-ink-900 mb-2">Inicia sesión</h2>
            <p className="text-ink-500">Accede al panel de actividad comercial.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label className="text-sm font-medium text-ink-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <Input
                  type="email"
                  placeholder="tu@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-white border-ink-200 text-ink-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-ink-700">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 bg-white border-ink-200 text-ink-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  required
                  minLength={8}
                  data-testid="input-password"
                />
              </div>
            </div>

            {error && (
              <div
                className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-brand-500 hover:bg-brand-600 text-white font-medium group transition-all shadow-sm"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Procesando…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Entrar
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-10 text-xs text-ink-400">
            © 2026 Inside CRM · Acceso restringido · Contacta con el administrador si no dispones de credenciales.
          </p>
        </div>
      </div>
    </div>
  );
};

const FeaturePill = ({ icon, label }) => (
  <div className="rounded-xl px-4 py-3 bg-white/5 border border-white/10 text-white text-sm flex items-center gap-2 backdrop-blur-sm">
    <span className="text-brand-500">{icon}</span>
    <span className="font-medium">{label}</span>
  </div>
);

export default Login;
